import RandomPersonnelGridOptions from "./RandomPersonnelModel/RandomPersonnelGridOptions"
import { ObservableFromFetch } from "../ObservableFromFetch"
import RandomPersonnelIdentifier from "./RandomPersonnelModel/RandomPersonnelIdentifier"
import RandomPersonnelGridRecord from "./RandomPersonnelModel/RandomPersonnelGridRecord"
import GridResponse from "../GridResponse"
import { ColumnFilters } from "./RandomPersonnelModel/ColumnFilters"
import { Filter } from "../GridRequest"

export type RandomPersonnelGridResponse = GridResponse<RandomPersonnelGridRecord> & ColumnFilters

function filters(filters: Filter[] = []) {
  return {
    Company: filters.find((filter) => filter.column === "Company")?.values || [],
    IDNumber: filters.find((filter) => filter.column === "IDNumber")?.values || [],
    Inactive: filters.find((filter) => filter.column === "Status")?.values.map((value) => value === "Inactive") || [],
    Location: filters.find((filter) => filter.column === "Location")?.values || [],
    Name: filters.find((filter) => filter.column === "Name")?.values || [],
    Region: filters.find((filter) => filter.column === "Region")?.values || [],
    Reg: filters.find((filter) => filter.column === "RegulatoryMode")?.values || [],
    Occupation: filters.find((filter) => filter.column === "Occupation")?.values || [],
    Category: filters.find((filter) => filter.column === "Category")?.values || [],
    Supervisor1: filters.find((filter) => filter.column === "Supervisor1")?.values || [],
    Supervisor2: filters.find((filter) => filter.column === "Supervisor2")?.values || [],
    HireDate: filters.find((filter) => filter.column === "HireDate")?.values || [],
    TerminationDate: filters.find((filter) => filter.column === "TerminationDate")?.values || [],
    PreEmploymentTestOnFile: filters.find((filter) => filter.column === "PreEmploymentTestOnFile")?.values || [],
    Gender: filters.find((filter) => filter.column === "Gender")?.values || [],
    DateOfBirth: filters.find((filter) => filter.column === "DateOfBirth")?.values || [],
    AddressLine1: filters.find((filter) => filter.column === "AddressLine1")?.values || [],
    City: filters.find((filter) => filter.column === "City")?.values || [],
    State: filters.find((filter) => filter.column === "State")?.values || [],
    Zip: filters.find((filter) => filter.column === "Zip")?.values || [],
    WorkPhone: filters.find((filter) => filter.column === "WorkPhone")?.values || [],
    CellPhone: filters.find((filter) => filter.column === "CellPhone")?.values || [],
    Email: filters.find((filter) => filter.column === "Email")?.values || [],
    User1: filters.find((filter) => filter.column === "User1")?.values || [],
    User2: filters.find((filter) => filter.column === "User2")?.values || [],
    User3: filters.find((filter) => filter.column === "User3")?.values || [],
    User4: filters.find((filter) => filter.column === "User4")?.values || [],
    User5: filters.find((filter) => filter.column === "User5")?.values || [],
    User6: filters.find((filter) => filter.column === "User6")?.values || [],
    User7: filters.find((filter) => filter.column === "User7")?.values || [],
    User8: filters.find((filter) => filter.column === "User8")?.values || [],
    User9: filters.find((filter) => filter.column === "User9")?.values || [],
    User10: filters.find((filter) => filter.column === "User10")?.values || [],
  }
}

function gridParams(options: RandomPersonnelGridOptions): string {
  let result = JSON.stringify({
    Exact: false,
    Filters: filters(options.filters),
    GridFilters: filters(options.columnFilters),
    Mask: options.search.text,
    PageNumber: options.pageNumber,
    PageSize: options.pageSize || 20,
    SearchBy: options.search.field || "All Personnel IDs",
    SortGrid: {
      SortColumn: options.sort?.column || "",
      SortOrder: options.sort?.order.toUpperCase() === "DESC" ? "DSC" : options.sort?.order.toUpperCase(),
    },
    WithInactive: true,
    IsConsortium: options.isConsortium,
    ForceReload: options.forceReload,
  })
  return result
}

function checkExist(options: RandomPersonnelGridOptions){
  return JSON.stringify({
    Exact: false,
    Filters: { Category: [], Company: [], IDNumber: [], Inactive: [], Location: [], Name: [], Region: [], Reg: [] },
    IsAddIndividualSearch: true,
    Mask: options.search.text,
    PageNumber: options.pageNumber,
    PageSize: options.pageSize,
    SearchBy: options.search.field,
    WithInactive: true,
    SortGrid: { SortColumn: "", SortOrder: "" }
  })
}

function exportParams(options: RandomPersonnelGridOptions): string {
  return JSON.stringify({
    Exact: false,
    Filters: filters(options.filters),
    GridFilters: filters(options.columnFilters),
    IsSearch: !!options.search.text,
    Mask: "",
    SearchBy: options.search.field || "All Personnel IDs",
    SearchString: options.search.text,
    WithInactive: true,
  })
}

function printParams(values: RandomPersonnelIdentifier[], options: RandomPersonnelGridOptions): string {
  return JSON.stringify({
    Filters: filters(options.filters),
    IsSearch: !!options.search.text,
    PersonnelID: values.map(value => { return {PersonID: value.PersonID, Mask: value.ID} }),
    SearchBy: options.search.field || "All Personnel IDs",
    SearchString: options.search.text
  })
}

export interface ConsortiumsMembership {
  PersonAssignedToPoolID: number
  PersonID: number
  PoolID: number
  Name: string
  Active: boolean
}

export function RetrieveConsortiumsMembership(PersonID: number) {
  const requestBody: string = new URLSearchParams({
    PersonID: PersonID.toString()
  }).toString()

  return ObservableFromFetch<ConsortiumsMembership[]>("WorkforceRandomTest/GetConsortiumsMembership?" + requestBody)
}

export function UpdateConsortiumsMembership(data: ConsortiumsMembership[]) {
  const params: string = JSON.stringify({
    Members: data
  })

  return ObservableFromFetch<string>("WorkforceRandomTest/UpdateConsortiumsMembership", params)
}

export const RandomPersonnelGridObservable = (params: RandomPersonnelGridOptions) => ObservableFromFetch<RandomPersonnelGridResponse>("WorkforceRandomTest/GetPersonnelGrid", gridParams(params))
export const RandomPersonnelExportCsv = (params: RandomPersonnelGridOptions) => ObservableFromFetch<string>("WorkforceRandomTest/ExportPersonnelRecord", exportParams(params))
export const RandomPersonnelPrint = (values: RandomPersonnelIdentifier[], params: RandomPersonnelGridOptions) => ObservableFromFetch<string>("WorkforceRandomTest/PrintPersonnelRecord", printParams(values, params))
export const RandomPersonnelAddPersonObservable = (params: RandomPersonnelGridOptions) => ObservableFromFetch<RandomPersonnelGridResponse>("WorkforceRandomTest/GetPersonnelGrid", checkExist(params))
